import React, { useEffect, useRef, useState } from "react";
import { Box, Paper, IconButton, Typography, TextField, CircularProgress, Divider, Chip, Card, CardContent } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import MemoryIcon from '@mui/icons-material/Memory';
import { useDispatch, useSelector } from "react-redux";
import default_theme from "assets/themes/themes";
import { closeChat, fetchHistoryRequest, fetchSuggestionsRequest, sendMessageRequest } from "../../redux/agents/agentsSlice";
import SourceDetailModal from "../SourceModal/SourceModal";

const SolutionChatPopup = ({ projectId, onAttachSolution }) => {
  const dispatch = useDispatch();
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState(null);
  const [loadingText, setLoadingText] = useState("");

  const { isOpen, mode, activeChallengeId, messages, loading, historyLoading, suggestions, suggestionsLoading } = useSelector((state) => state.agents);
  const isSolutionMode = isOpen && mode === "solutions";

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, isSolutionMode]);

  useEffect(() => {
    if (isSolutionMode && projectId && activeChallengeId) {
      // 1. Fetch Chat History
      dispatch(fetchHistoryRequest({ projectId, mode: "solutions", challengeId: activeChallengeId }));
      // 2. Fetch Direct Vector Suggestions (Top 5)
      dispatch(fetchSuggestionsRequest({ projectId, challengeId: activeChallengeId }));
    }
  }, [isSolutionMode, projectId, activeChallengeId, dispatch]);

  useEffect(() => {
    let interval;
    
    if (loading) {
      const phrases = [
        "Thinking...",
        "Searching the solution database...",
        "Analyzing database results...",
        "Formatting the final answer...",
        "Refining insights...",
        "Almost there..."
      ];
      
      let currentIndex = 0;
      setLoadingText(phrases[0]);
      
      // Change the text every 12 seconds in a loop
      interval = setInterval(() => {
        currentIndex = (currentIndex + 1) % phrases.length;
        setLoadingText(phrases[currentIndex]);
      }, 12000);
      
    } else {
      setLoadingText("");
    }
    
    return () => clearInterval(interval);
  }, [loading]);

  const handleSend = () => {
    if (!input.trim() || loading) return;
    dispatch(sendMessageRequest({ projectId, message: input.trim(), mode: "solutions", challengeId: activeChallengeId }));
    setInput("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleOpenSource = (sourceId) => {
    setSelectedSourceId(sourceId);
    setModalOpen(true);
  };

  if (!isSolutionMode) return null;

  return (
    <>
      <Paper
        elevation={6}
        sx={{
          position: "fixed", bottom: 24, right: 24, width: 450, height: 750,
          display: "flex", flexDirection: "column", borderRadius: 3, overflow: "hidden", zIndex: 1300,
        }}
      >
        <Box sx={{ p: 2, backgroundColor: default_theme.palette.other.chatGreen || "#13A89E", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SmartToyIcon />
            <Typography variant="subtitle1" fontWeight={600}>AI Solution Suggestions</Typography>
          </Box>
          <IconButton size="small" onClick={() => dispatch(closeChat())} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
          
          {/* DIRECT SUGGESTIONS SECTION */}
          <Box sx={{ p: 2, backgroundColor: "#F0Fdfa", borderBottom: "1px solid #e2e8f0" }}>
            <Typography variant="subtitle2" sx={{ color: "#0f766e", mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
              <MemoryIcon fontSize="small"/> Top 5 Matches
            </Typography>
            
            {suggestionsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={24} /></Box>
            ) : suggestions.length === 0 ? (
              <Typography variant="body2" color="textSecondary">No matches found.</Typography>
            ) : (
              <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1, '&::-webkit-scrollbar': { height: 6 }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: 4 } }}>
                {suggestions.map((sug, idx) => {
                   // Ensure score defaults to 0 if null, and clamp max at 1.0 just in case
                   const validScore = sug.score || 0;
                   const percentage = Math.min((validScore * 100), 100).toFixed(0);
                   
                   return (
                    <Card key={idx} variant="outlined" sx={{ minWidth: 220, maxWidth: 220, borderRadius: 2, flexShrink: 0, display: "flex", flexDirection: "column" }}>
                      <CardContent sx={{ p: 1.5, pb: "12px !important", flex: 1, display: "flex", flexDirection: "column" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                          <Chip 
                            label={`${percentage}% Match`} 
                            size="small" 
                            sx={{ fontSize: "0.65rem", height: 20, fontWeight: 700, backgroundColor: percentage >= 80 ? "#dcfce7" : "#fef9c3", color: percentage >= 80 ? "#166534" : "#854d0e" }} 
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                          {sug.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary", mb: 1, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                          {sug.summary}
                        </Typography>
                        <Box sx={{ mt: "auto", pt: 1 }}>
                          <Chip label="View & Attach" size="small" onClick={() => handleOpenSource(sug.id)} sx={{ width: "100%", backgroundColor: "#e2e8f0", "&:hover": { backgroundColor: "#cbd5e1" }, cursor: "pointer", fontWeight: 600 }} />
                        </Box>
                      </CardContent>
                    </Card>
                  )
                })}
              </Box>
            )}
          </Box>

          {/* CHAT AGENT SECTION */}
          <Box sx={{ p: 2, flex: 1, backgroundColor: "#F8FAFC" }}>
            {historyLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}><CircularProgress size={24} /></Box>
            ) : (
              <>
                <Typography variant="caption" sx={{ display: "block", textAlign: "center", mb: 3, color: "text.disabled" }}>
                  Have questions about these solutions? Chat below.
                </Typography>
                {messages.map((msg, index) => (
                  <Box key={index} sx={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", mb: 2 }}>
                    <Box sx={{ maxWidth: "85%", p: 1.5, borderRadius: 2, backgroundColor: msg.role === "user" ? "#13A89E" : "white", color: msg.role === "user" ? "white" : "text.primary", boxShadow: "0 1px 2px rgba(0,0,0,0.1)" }}>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{msg.content}</Typography>
                      
                      {/* Render source chips with actual titles! */}
                      {msg.sources && msg.sources.length > 0 && (
                        <Box sx={{ mt: 1, pt: 1, display: "flex", flexWrap: "wrap", gap: 1, borderTop: msg.role === "user" ? "1px solid rgba(255,255,255,0.2)" : "1px solid #f1f5f9" }}>
                          {msg.sources.map((src, idx) => (
                            <Chip
                              key={idx}
                              label={src.title} // Shows the actual solution name
                              size="small"
                              onClick={() => handleOpenSource(src.id)} 
                              sx={{ 
                                backgroundColor: msg.role === "user" ? "rgba(255,255,255,0.2)" : "#f1f5f9", 
                                color: msg.role === "user" ? "white" : "text.primary",
                                cursor: "pointer",
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                "&:hover": { backgroundColor: msg.role === "user" ? "rgba(255,255,255,0.3)" : "#e2e8f0" }
                              }}
                            />
                          ))}
                        </Box>
                      )}
                      
                    </Box>
                  </Box>
                ))}
              </>
            )}
            {loading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, color: "text.secondary" }}>
                <CircularProgress size={16} />
                <Typography variant="caption">{loadingText}</Typography> 
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>
        </Box>

        <Divider />

        <Box sx={{ p: 2, backgroundColor: "white", display: "flex", alignItems: "flex-end", gap: 1 }}>
          <TextField
            fullWidth size="small" multiline maxRows={3} placeholder="Ask the agent a question..."
            value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyPress}
          />
          <IconButton color="primary" disabled={!input.trim() || loading} onClick={handleSend} sx={{ backgroundColor: default_theme.palette.other.figmaBlue, borderRadius: 2 }}>
            <SendIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Internal Modal Rendering */}
      {selectedSourceId && (
        <SourceDetailModal
          open={modalOpen}
          onClose={() => { setModalOpen(false); setSelectedSourceId(null); }}
          source={{ entry_id: selectedSourceId }}
          onAttach={onAttachSolution}
        />
      )}
    </>
  );
};

export default SolutionChatPopup;